/**
 * F2 — PRAYER REQUESTS for an activity (prayer meeting).
 *
 * REAL PRIVACY lives in the engine: a private request NEVER reaches an
 * unauthorized device's SQLite (streams `my_prayer_requests` /
 * `prayer_public_*` / `prayer_private_*`), and the write-path only lets the
 * REQUESTER or `prayer:manage` edit (owner_stamp). Here the same rule is
 * re-applied defensively (`visiblePrayerRequests`) and the actions are gated.
 */
import { msg } from "@lingui/core/macro";
import { type Accessor, createMemo, createSignal, For, Show } from "solid-js";
import {
  canEditPrayerRequest,
  getPrayerRequestsQuery,
  isAnswered,
  isPrivatePrayer,
  patchRowVerified,
  queryKeys,
  togglePrayerStatus,
  visiblePrayerRequests,
} from "@agape/shared";
import {
  type MeetingActivityRecord,
  prayer_request,
  type PrayerRequestRecord,
} from "@agape/powersync";
import { CheckCircle2, HandHelping, Lock, Trash2 } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog, TextArea } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

export function PrayerSection(props: { activity: Accessor<MeetingActivityRecord> }) {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();
  const session = useSession();
  const { userId } = session;

  const requests = useDomainQuery<PrayerRequestRecord>(
    (uid) => queryKeys.domain.prayerRequests(uid),
    (db) => getPrayerRequestsQuery(db),
  );

  const scope = () => [props.activity().group_id, props.activity().church_id];
  const canModerate = () => grants.can(scope(), "prayer", "manage");
  const canRequest = () => grants.can(scope(), "prayer", "write");
  const viewer = () => ({ userId: userId(), canModerate: canModerate() });

  const visible = createMemo(() =>
    visiblePrayerRequests(requests.data ?? [], props.activity().id, viewer()),
  );

  // ── Request prayer ─────────────────────────────────────────────────────────
  const [open, setOpen] = createSignal(false);
  const [content, setContent] = createSignal("");
  const [isPrivate, setIsPrivate] = createSignal(false);
  const openAdd = () => {
    setContent("");
    setIsPrivate(false);
    setOpen(true);
  };
  const addRequest = async () => {
    if (!content().trim()) {
      return;
    }
    await write()
      .insert(prayer_request)
      .values({
        id: newId(),
        activity_id: props.activity().id,
        church_id: props.activity().church_id,
        group_id: props.activity().group_id,
        // Local optimism: the SERVER re-stamps owner and name (owner_stamp).
        requester_user_id: userId(),
        requester_name: session.data?.user?.name ?? null,
        content: content().trim(),
        is_private: isPrivate() ? 1 : 0,
        status: "praying",
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setOpen(false);
    showToast(
      isPrivate()
        ? t(msg`Private request sent — only you and the pastors can see it.`)
        : t(msg`Prayer request added`),
      "success",
    );
  };

  const toggleStatus = async (row: PrayerRequestRecord) => {
    const next = togglePrayerStatus(row, nowIso());
    try {
      await patchRowVerified(write(), prayer_request, prayer_request.id, row.id, {
        status: next.status,
        answered_at: next.answered_at,
      });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };
  const trash = async (row: PrayerRequestRecord) => {
    try {
      await patchRowVerified(write(), prayer_request, prayer_request.id, row.id, {
        deleted_at: nowIso(),
      });
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };

  return (
    <div class="space-y-2 border-t border-border pt-2" data-testid="prayer-section">
      <div class="flex items-center justify-between">
        <p class="text-xs font-bold uppercase tracking-wider opacity-60">
          {t(msg`Prayer requests`)}
        </p>
        <Show when={canRequest()}>
          <Button size="sm" variant="ghost" data-testid="prayer-add" onClick={openAdd}>
            <HandHelping size={14} /> {t(msg`Request prayer`)}
          </Button>
        </Show>
      </div>
      <Show
        when={visible().length > 0}
        fallback={
          <p class="text-xs text-muted-foreground" data-testid="prayer-empty">
            {t(msg`No prayer requests yet.`)}
          </p>
        }
      >
        <For each={visible()}>
          {(row) => (
            <div
              class="flex items-start justify-between gap-2 rounded-md bg-accent/5 px-2.5 py-2"
              data-testid="prayer-item"
              data-prayer-status={row.status}
              data-prayer-private={isPrivatePrayer(row) ? "true" : "false"}
            >
              <div class="min-w-0">
                <p class={`text-sm ${isAnswered(row) ? "line-through opacity-60" : ""}`}>
                  {row.content}
                </p>
                <p class="flex items-center gap-1 text-xs text-muted-foreground">
                  <Show when={isPrivatePrayer(row)}>
                    <Lock size={11} aria-label={t(msg`Private`)} />
                  </Show>
                  {row.requester_name ?? t(msg`Someone`)}
                  {" · "}
                  <span data-testid="prayer-status">
                    {isAnswered(row) ? t(msg`Answered`) : t(msg`Praying`)}
                  </span>
                </p>
              </div>
              <Show when={canEditPrayerRequest(row, viewer())}>
                <div class="flex shrink-0 gap-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={
                      isAnswered(row) ? t(msg`Mark as praying`) : t(msg`Mark as answered`)
                    }
                    data-testid="prayer-answer-toggle"
                    onClick={() => void toggleStatus(row)}
                  >
                    <CheckCircle2 size={14} />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t(msg`Move request to trash`)}
                    data-testid="prayer-trash"
                    onClick={() => void trash(row)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </Show>
            </div>
          )}
        </For>
      </Show>

      <FormDialog
        open={open()}
        onOpenChange={setOpen}
        title={t(msg`Request prayer`)}
        icon={<HandHelping size={20} />}
        submitTestId="prayer-submit"
        disabled={!content().trim()}
        onSubmit={addRequest}
      >
        <Field id="pr-content" label={t(msg`What should we pray for?`)}>
          <TextArea
            id="pr-content"
            rows={3}
            value={content()}
            onInput={setContent}
            class="font-sans"
            testId="prayer-content-input"
          />
        </Field>
        <label
          class="flex cursor-pointer items-center gap-2 text-sm"
          data-testid="prayer-private-toggle"
        >
          <Checkbox checked={isPrivate()} onChange={setIsPrivate} aria-label={t(msg`Private`)} />
          <span>{t(msg`Private — only visible to you and the pastors`)}</span>
        </label>
      </FormDialog>
    </div>
  );
}
